import { Component, OnInit, OnDestroy } from '@angular/core';
import { Messaging, onMessage, MessagePayload, NotificationPayload } from '@angular/fire/messaging';
import { Router } from '@angular/router';
import { GlobalService } from '@cfg-services/global/global.service';
import { environment } from 'src/environments/environment';
import { User } from '@angular/fire/auth';
import { AuthenticationService } from '@cfg-services/authentication/authentication.service';
import { NotificationService } from '@cfg-services/notifications/notification.service';
import { AnalyticsService } from '@cfg-services/analytics/analytics.service';
import { UserSessionEvent } from '@cfg-interface/analytics.interface';
import { PwaService } from '@cfg-services/pwa/pwa.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {

  title = 'CFG-Client';
  ENV_NAME: string = environment.name;
  DISPLAY_ENV_BAR: boolean = false;
  AUTH_STATE: User | null = null;
  CURRENT_OS: string = '';
  IS_PWA_INSTALLED: boolean = false;

  constructor(
              private messaging: Messaging,
              private router: Router,
              private globalService: GlobalService,
              private authService: AuthenticationService,
              private notificationService: NotificationService,
              private analyticsService: AnalyticsService,
              private pwaService: PwaService
              ) {}

  ngOnInit(): void {
    this.startPWALifeCycle();
    this.getNotifications();
    this.setEnvBar();
    this.getAuthState();
  }

  // PWA

  startPWALifeCycle(): void {
    this.getCurrentOS();
  }

  getCurrentOS(): void {
    this.CURRENT_OS = this.pwaService.getDeviceOS();
    this.checkPWAInstance(this.CURRENT_OS);
  }

  checkPWAInstance(currentOS: string): void {
    if (currentOS === 'Android' || currentOS === 'Other') {
      this.IS_PWA_INSTALLED = this.pwaService.checkAndroidOrWindowsPWAInstance();
    } else {
      // Checking in iOS devices
      
      // Checking in iOS devices
    }
  }

  // PWA

  ngOnDestroy(): void {
    // Cleanup si es necesario
  }

  getAuthState(): void {
    this.authService.getAuthState().subscribe((response: User | null) => {
      if (response) {
        this.AUTH_STATE = response;
        this.authService.startToMonitorSession(response.uid);
        const USER_SESSION_EVENT: UserSessionEvent = {
          customer_uid: this.AUTH_STATE.uid
        };
        this.analyticsService.logUserEvent(USER_SESSION_EVENT)
      }
    });
  }

  setEnvBar(): void {
    this.DISPLAY_ENV_BAR = this.globalService.displayEnvironmentBar();
  }

  getNotifications(): void {
    try {
      // Verificar capacidades de notificación
      const capabilities = this.notificationService.getNotificationCapabilities();
      // // // console.log('🔔 CFG Capacidades de notificación:', capabilities);

      if (!capabilities.canUse) {
        // console.warn('⚠️ Notificaciones no disponibles:', capabilities.message);
        return;
      }

      onMessage(this.messaging, (message: MessagePayload) => {
        let chat_id: string = '';
        if (message.data !== null && message.data !== undefined) {
          chat_id = message.data['chat_id'];
        }
        this.createNewLocalNotification(message.notification, chat_id);
      });
    } catch (error) {
      // console.error('🔥 Error configurando notificaciones:', error);
      // No fallar la aplicación por problemas de notificaciones
    }
  }

  createNewLocalNotification(notification: NotificationPayload | undefined, chat_id?: string): void {
    try {
      // Verificar si las notificaciones están soportadas
      if (!this.notificationService.canUseNotifications()) {
        // console.warn('⚠️ No se puede crear notificación local: API no soportada');
        // En iOS, mostrar alternativa (banner visual)
        this.showIOSAlternativeNotification(notification, chat_id);
        return;
      }

      // Crear notificación solo si está soportada
      if ('Notification' in window && Notification.permission === 'granted') {
        const LOCAL_NOTIFICATION: Notification = new Notification(`${notification?.title}`, {
          body: notification?.body,
          icon: notification?.icon || '/assets/icons/icon-128x128.png',
          tag: 'cfg-notification',
          requireInteraction: false
        });

        LOCAL_NOTIFICATION.onclick = () => {
          window.focus(); // Enfocar la ventana
          if (chat_id) {
            this.router.navigate(['/chats'], { queryParams: { id: JSON.parse(`${chat_id}`) } });
          }
          LOCAL_NOTIFICATION.close();
        };

        // Auto-cerrar después de 5 segundos
        setTimeout(() => {
          LOCAL_NOTIFICATION.close();
        }, 5000);
      }
    } catch (error) {
      // console.error('🔥 Error creando notificación local:', error);
      // Fallback para iOS u otros errores
      this.showIOSAlternativeNotification(notification, chat_id);
    }
  }

  private showIOSAlternativeNotification(notification: NotificationPayload | undefined, chat_id?: string): void {
    // Alternativa para iOS: mostrar un banner temporal
    // // // console.log('📱 Mostrando notificación alternativa para iOS:', notification?.title);

    // Crear un banner visual temporal
    const banner = document.createElement('div');
    banner.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc3545;
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10000;
      max-width: 300px;
      cursor: pointer;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      transition: all 0.3s ease;
    `;

    banner.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 5px;">${notification?.title || 'Nueva notificación'}</div>
      <div style="opacity: 0.9;">${notification?.body || ''}</div>
    `;

    banner.onclick = () => {
      if (chat_id) {
        this.router.navigate(['/chats'], { queryParams: { id: JSON.parse(`${chat_id}`) } });
      }
      document.body.removeChild(banner);
    };

    document.body.appendChild(banner);

    // Auto-remover después de 5 segundos
    setTimeout(() => {
      if (document.body.contains(banner)) {
        banner.style.opacity = '0';
        setTimeout(() => {
          if (document.body.contains(banner)) {
            document.body.removeChild(banner);
          }
        }, 300);
      }
    }, 5000);
  }

}
